import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ScreeningResultsService } from './services/screening-results/screening-results.service';
import { ScreenBand } from './molecules/screen-band/screen-band';
import { ScreenKpis } from './molecules/screen-kpis/screen-kpis';
import { ScreenContent } from './molecules/screen-content/screen-content';
import { ScreenCreators } from './molecules/screen-creators/screen-creators';
import { ScreenBriefManagement } from './molecules/screen-brief-management/screen-brief-management';
import { ScreeningTabDef, ScreeningTabs } from './ui/screening-tabs/screening-tabs';

export type ScreenTab = 'brief_management' | 'content' | 'creators';

const VALID_TABS: readonly ScreenTab[] = ['brief_management', 'content', 'creators'];

/** Old deep links, and the retired Briefing tab, both land on Brief Management. */
const TAB_ALIASES: Record<string, ScreenTab> = {
  brief: 'brief_management',
  briefing: 'brief_management',
};

/**
 * Manage Screening Brief — the hub for one brief.
 *
 * Three tabs, per the design: the brief and the rules it was turned into, the content that tripped
 * those rules, and the creators being screened.
 */
@Component({
  selector: 'app-manage-brief-tabs-screen',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ScreenBand, ScreenKpis, ScreeningTabs, ScreenBriefManagement, ScreenContent, ScreenCreators],
  templateUrl: './manage-brief-tabs-screen.html',
  styleUrl: './manage-brief-tabs-screen.scss',
})
export class ManageBriefTabsScreen {

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);
  readonly results = inject(ScreeningResultsService);

  readonly selectedTab = signal<ScreenTab>('brief_management');

  /** Set when the band's "Add creators" is pressed, so the Creators panel opens its picker. */
  readonly openCreatorPicker = signal<boolean>(false);

  readonly tabs = computed<ScreeningTabDef[]>(() => {
    const kpis = this.results.kpis();
    return [
      { id: 'brief_management', label: 'Brief Management', count: this.results.rules().length, countNoun: 'rules' },
      { id: 'content', label: 'Content', count: kpis.flags, countNoun: 'flags', alert: kpis.flags > 0 },
      { id: 'creators', label: 'Creators', count: kpis.creators, countNoun: 'creators' },
    ];
  });

  readonly panelAnnouncement = computed(() => {
    const tab = this.tabs().find((candidate) => candidate.id === this.selectedTab());
    return tab ? `${tab.label} panel, ${tab.count} ${tab.countNoun}` : '';
  });

  constructor() {
    // paramMap, not the snapshot: the component is reused across a `:tab` change, so a snapshot
    // read would never see the new tab and browser Back between tabs would do nothing.
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => this.applyParams(params));
  }

  private applyParams(params: ParamMap): void {
    const briefId = params.get('briefId') ?? undefined;
    if (briefId) {
      this.results.load(briefId);
    }
    this.selectedTab.set(this.parseTab(params.get('tab')));
  }

  private parseTab(raw: string | null): ScreenTab {
    if (!raw) {
      return 'brief_management';
    }
    const aliased = TAB_ALIASES[raw] ?? raw;
    return VALID_TABS.includes(aliased as ScreenTab) ? (aliased as ScreenTab) : 'brief_management';
  }

  selectTab(tab: string): void {
    if (!VALID_TABS.includes(tab as ScreenTab) || tab === this.selectedTab()) {
      return;
    }
    this.selectedTab.set(tab as ScreenTab);
    // replaceUrl so tab switching stays deep-linkable without filling the history stack.
    void this.router.navigate(['/manage-brief-screen', this.results.briefId(), tab], { replaceUrl: true });
  }

  requestAddCreators(): void {
    this.openCreatorPicker.set(true);
    this.selectTab('creators');
  }

  onCreatorPickerOpened(): void {
    this.openCreatorPicker.set(false);
  }

  goBack(): void {
    this.location.back();
  }
}
